import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import TransferResultsTable, { transferResultRowShape } from 'common/modules/TransferResultsTable';
import { pluralizeOrders } from 'common/utils/transferOutcomes';
import PropTypes from 'prop-types';

/**
 * Post-transfer results modal. Shown for a PARTIAL transfer and for a fully blocked MULTI-order
 * batch — the cases where a snackbar cannot tell the operator which orders failed.
 *
 * Chrome deliberately mirrors CentralFillSendDialog (the compounding send flow) so the two bulk
 * flows read the same, but the components stay separate: that dialog is a pre-flight eligibility
 * PREVIEW keyed by line item, this one is a post-action RESULT keyed by order.
 *
 * The caller owns cleanup: `onClose` is what completes the selection-clear and queue refresh for a
 * partial transfer, so the operator can read the failures while the queue still shows them.
 */
const TransferResultsModal = ({
  open,
  onClose,
  rows,
  totalRequested,
  totalTransferred,
  destinationLabel,
}) => {
  const hasTransferred = totalTransferred > 0;
  const title = hasTransferred ? 'Transfer Results' : 'Transfer Not Allowed';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{ sx: { maxHeight: '88vh', borderRadius: 3 } }}
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
      >
        <Box>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>
            {`${pluralizeOrders(totalTransferred)} of ${totalRequested} transferred${
              destinationLabel ? ` to ${destinationLabel}` : ''
            }.`}
          </Typography>
        </Box>
        <IconButton
          // Distinct from the footer's "Close" button so the two do not share an accessible name.
          aria-label='close transfer results'
          onClick={onClose}
          size='small'
          sx={{
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <CloseIcon fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box
          sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5 }}
          aria-live='polite'
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              borderRadius: '0 10px 10px 0',
              px: 2,
              py: 1.25,
              background: 'linear-gradient(135deg, #fffbeb 0%, #fefce8 100%)',
              border: '1px solid rgba(245,158,11,0.2)',
              borderLeftWidth: '4px',
              borderLeftColor: '#f59e0b',
            }}
          >
            <WarningAmberIcon sx={{ color: '#d97706', fontSize: '1.1rem', flexShrink: 0 }} />
            <Typography
              sx={{ fontSize: '0.78rem', lineHeight: 1.5, color: '#92400e', fontWeight: 500 }}
            >
              {hasTransferred
                ? 'Orders that were not transferred remain at their current facility. Review the reasons below, then choose a different facility for those orders if needed.'
                : 'No orders were transferred. They all remain at their current facility — review the reasons below and choose a different facility.'}
            </Typography>
          </Box>

          <Box
            sx={{ flex: 1, minHeight: 0, display: 'flex', '& > div': { flex: 1, minHeight: 0 } }}
          >
            <TransferResultsTable rows={rows} maxHeight='100%' />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between', bgcolor: '#f8f9fb' }}>
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
            {totalRequested - totalTransferred}
          </Box>
          {' of '}
          <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
            {totalRequested}
          </Box>
          {` order${totalRequested === 1 ? '' : 's'} could not be transferred`}
        </Typography>

        <Stack direction='row' spacing={1.5}>
          <Button
            variant='contained'
            onClick={onClose}
            sx={{ bgcolor: '#201d52', borderRadius: 2, '&:hover': { bgcolor: '#2c2870' } }}
          >
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

TransferResultsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(transferResultRowShape).isRequired,
  totalRequested: PropTypes.number.isRequired,
  totalTransferred: PropTypes.number.isRequired,
  destinationLabel: PropTypes.string,
};

TransferResultsModal.defaultProps = { destinationLabel: '' };

export default TransferResultsModal;
